/* eslint @typescript-eslint/strict-boolean-expressions: off */

import React, { useEffect, useState } from "react";
import styles from "./NewFeature.module.css";
import { ControlledClickPopover } from "components/ClickPopover";
import { Button } from "components/Button/Button";
import {
  tryLocalStorageGetItem,
  tryLocalStorageSetItem,
} from "lib/storage/storage";
import { useWindowSize } from "@reach/window-size";
import { Position } from "shared/getPosition/getPosition";
import {
  removeFromHighlightQueue,
  addToHighlightQueue,
  useHighlightQueue,
} from "./store";

interface Props {
  title: string;
  description: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactElement;
  position?: Position;
  feature: string; // this will be used as key to store in localStorage
  imgSrc?: string;
}

export function NewFeatureHighlightLocal(props: Props) {
  const { feature, children, position = "below-align-left" } = props;
  const [dismissed, setDismissed] = useState(
    tryLocalStorageGetItem(feature) === "true"
  );
  const activeHighlight = useHighlightQueue();
  const [isOpen, setIsOpen] = useState(!dismissed);
  const isMobile = useWindowSize().width < 499;

  const isHidden = dismissed || !isOpen || isMobile;

  const handleClose = () => {
    setIsOpen(false);
    tryLocalStorageSetItem(feature, "true");
    setDismissed(true);

    removeFromHighlightQueue(feature);
  };

  useEffect(() => {
    if (!isHidden) {
      addToHighlightQueue(feature);

      return () => {
        removeFromHighlightQueue(feature);
      };
    }
  }, [isHidden]);

  if (isHidden || feature !== activeHighlight) {
    return <>{children}</>;
  }

  return (
    <>
      <div className={styles.overlay} />
      <ControlledClickPopover
        isOpen={isOpen}
        disableEscKey
        setIsOpen={setIsOpen}
        position={position}
        content={() => <Content {...props} handleClose={handleClose} />}
        closeOnClickOutside={false}
      >
        <div className={styles.highlightedElement}>{children}</div>
      </ControlledClickPopover>
    </>
  );
}

function Content(props: Props & { handleClose: () => void }) {
  const { title, description, actions, imgSrc, handleClose } = props;
  return (
    <div className={styles.content}>
      <div>
        <h2>{title}</h2>
        <div className={styles.description}>{description}</div>
      </div>

      <div className={styles.actions}>
        <Button size="M" theme="primary" onClick={handleClose}>
          Got it
        </Button>
        {actions}
      </div>

      {imgSrc && (
        <div className={styles.image}>
          <img src={imgSrc} />
        </div>
      )}
    </div>
  );
}
